/**
 * Challenge generation for the drum game.
 *
 * Ten challenges per instrument, built from a difficulty curve rather than
 * hand-authored, so every drum gets a full set regardless of how many pads it
 * has — the twelve instruments carry between two and four each.
 *
 * Generation is deterministic: the same challenge id always yields the same
 * rhythm. Scores are compared on a leaderboard, so a pattern that changed
 * between reloads would make those comparisons meaningless.
 */

import { AFRICAN_INSTRUMENTS, AfricanInstrument } from '../../../data/africanInstruments';

/** One stroke in a challenge: which pad, and when. */
export interface ChallengeStep {
  /** Index into the instrument's own `pads` array. */
  padIndex: number;
  /** Milliseconds from the start of the phrase. */
  timeMs: number;
}

export interface DrumChallenge {
  /** `${instrumentSlug}-${index}`, e.g. "djembe-3". */
  id: string;
  instrumentSlug: string;
  /** 1–10. */
  index: number;
  name: string;
  bpm: number;
  steps: ChallengeStep[];
  /** Score worth aiming for — a clean run at full combo. */
  targetScore: number;
}

/** How many challenges each instrument gets. */
export const CHALLENGES_PER_INSTRUMENT = 10;

/**
 * The shape of one rung on the difficulty ladder. `beats` counts quarter-note
 * slots; `maxPads` caps how much of the kit the phrase may use, and is clamped
 * again to the pads the instrument actually has.
 */
interface Rung {
  beats: number;
  bpm: number;
  maxPads: number;
  /** Chance a step lands on an off-beat eighth rather than the beat. */
  syncopation: number;
  /** Chance a step is immediately echoed a sixteenth later. */
  doubles: number;
}

const LADDER: Rung[] = [
  { beats: 16, bpm: 60,  maxPads: 2, syncopation: 0,    doubles: 0 },
  { beats: 16, bpm: 70,  maxPads: 2, syncopation: 0,    doubles: 0 },
  { beats: 20, bpm: 80,  maxPads: 3, syncopation: 0.15, doubles: 0 },
  { beats: 24, bpm: 88,  maxPads: 3, syncopation: 0.2,  doubles: 0 },
  { beats: 24, bpm: 95,  maxPads: 3, syncopation: 0.25, doubles: 0.1 },
  { beats: 28, bpm: 100, maxPads: 4, syncopation: 0.3,  doubles: 0.15 },
  { beats: 28, bpm: 108, maxPads: 4, syncopation: 0.35, doubles: 0.2 },
  { beats: 32, bpm: 115, maxPads: 4, syncopation: 0.4,  doubles: 0.25 },
  { beats: 32, bpm: 120, maxPads: 4, syncopation: 0.45, doubles: 0.3 },
  { beats: 36, bpm: 130, maxPads: 4, syncopation: 0.5,  doubles: 0.35 },
];

/** Names run easiest to hardest, so the list reads as a progression. */
const NAMES = [
  'First Steps',
  'Steady Hands',
  'Finding the Off-beat',
  'Call and Answer',
  'Rolling',
  'Cross Rhythm',
  'Driving',
  'Tight Turns',
  'Full Voice',
  'Master Phrase',
];

/**
 * mulberry32 — a compact seeded PRNG. Any small deterministic generator would
 * do; this one is short enough to keep inline and avoids a dependency.
 */
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Stable 32-bit hash of the challenge id, used to seed the generator. */
function hashSeed(text: string): number {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function buildSteps(
  rung: Rung,
  padCount: number,
  random: () => number,
): ChallengeStep[] {
  const beatMs = 60_000 / rung.bpm;
  const usablePads = Math.min(rung.maxPads, padCount);
  const steps: ChallengeStep[] = [];

  for (let beat = 0; beat < rung.beats; beat += 1) {
    // Off-beat placement shifts the stroke half a beat later.
    const offbeat = random() < rung.syncopation;
    const timeMs = Math.round((beat + (offbeat ? 0.5 : 0)) * beatMs);
    const padIndex = Math.floor(random() * usablePads);

    steps.push({ padIndex, timeMs });

    // A double is a quick echo of the same pad a sixteenth later. Skipped on
    // the final beat so a phrase never ends on a dangling flam.
    if (beat < rung.beats - 1 && random() < rung.doubles) {
      steps.push({
        padIndex,
        timeMs: Math.round(timeMs + beatMs / 4),
      });
    }
  }

  // Two strokes closer than this are indistinguishable to play, and would make
  // the nearest-step search ambiguous while judging.
  const MIN_GAP_MS = 90;
  const spaced = steps
    .sort((a, b) => a.timeMs - b.timeMs)
    .filter((step, i, all) => i === 0 || step.timeMs - all[i - 1].timeMs >= MIN_GAP_MS);

  return spaced;
}

/** Build the full set of challenges for one instrument. */
function generate(instrument: AfricanInstrument): DrumChallenge[] {
  return LADDER.map((rung, i) => {
    const index = i + 1;
    const id = `${instrument.slug}-${index}`;
    const steps = buildSteps(rung, instrument.pads.length, mulberry32(hashSeed(id)));

    return {
      id,
      instrumentSlug: instrument.slug,
      index,
      name: NAMES[i],
      bpm: rung.bpm,
      steps,
      // A clean run before any combo multiplier; enough to aim at without
      // implying the multiplier is unreachable.
      targetScore: steps.length * 100,
    };
  });
}

/**
 * Every challenge, generated once at module load. The set is fixed and small
 * (12 instruments x 10), so there is nothing to gain from generating lazily.
 */
const BY_INSTRUMENT = new Map<string, DrumChallenge[]>(
  AFRICAN_INSTRUMENTS.map((instrument) => [instrument.slug, generate(instrument)]),
);

const BY_ID = new Map<string, DrumChallenge>(
  [...BY_INSTRUMENT.values()].flat().map((challenge) => [challenge.id, challenge]),
);

/** The ten challenges for an instrument, easiest first. */
export function getChallenges(instrumentSlug: string): DrumChallenge[] {
  return BY_INSTRUMENT.get(instrumentSlug) ?? [];
}

/** Look up a single challenge by its id. */
export function getChallenge(id: string): DrumChallenge | undefined {
  return BY_ID.get(id);
}

/** Total playable challenges across all instruments. */
export const TOTAL_CHALLENGES = BY_ID.size;
